<template>
  <div class="card border-0 rounded-lg">
    <div v-if="loading && !list.length" class="d-flex justify-center">
      <i class="far fa-spinner-third fa-spin"></i>
    </div>
    <div v-else class="card-list">
      <div class="card-list-header d-flex justify-content-between align-items-top mb-2">
        <div v-if="opportunityState">
          <h6 class="m-0" :class="[opportunityState.notActiveOpportunities ? 'text-success' : 'text-secondary']">
            {{ opportunityStateLabel || 'Opportunities' }}
          </h6>
          <small>{{ listCount }} {{ opportunityState.notActiveOpportunities ? 'Closed' : 'Opportunities' }}</small>
        </div>
        <div v-else>
          <h6 class="m-0">Opportunities</h6>
        </div>
        <a @click="addNewOpportunity()" class="btn btn-link">
          <i
            :class="opportunity ? 'far fa-times fa-fw' : 'far fa-plus fa-fw'"
            title="Add Opportunity"
            v-b-tooltip.hover
          ></i>
        </a>
      </div>
      <div class="alert alert-info d-flex justify-content-between align-items-center border-0">
        <div class="small">Annualized value</div>
        <div class="font-weight-bold">{{ annualizedValue || 0 | currency }}</div>
      </div>
      <div class="card-list-body">
        <Container
          :group-name="'opportunities'"
          :get-child-payload="getChildPayload"
          @drop="onDrop"
          v-if="sortParams.orderBy === 'sortIndex'"
        >
          <Draggable v-for="item in list" :key="item._id">
            <div class="card border-0 rounded-lg card-kanban text-decoration-none text-secondary mb-3 p-3">
              <OpportunityItem
                :opportunity="item"
                :visible-opportunity-state="!opportunityState"
                @remove="deleteOpportunity(item)"
                @edit="editOpportunity(item)"
              />
            </div>
          </Draggable>
        </Container>
        <div v-else>
          <div v-for="item in list" :key="item._id">
            <div class="card border-0 rounded-lg card-kanban text-decoration-none text-secondary mb-3 p-3">
              <OpportunityItem
                :opportunity="item"
                :visible-opportunity-state="!opportunityState"
                @remove="deleteOpportunity(item)"
                @edit="editOpportunity(item)"
              />
            </div>
          </div>
        </div>
        <div v-if="canLoadMore" class="mt-2 d-flex justify-content-end">
          <b-button :disabled="loading" @click="loadNextOpportunities" size="sm" variant="link" block>
            Load More
          </b-button>
        </div>
      </div>
    </div>
    <b-modal
      ref="modal"
      title="Opportunity"
      size="md"
      hide-footer
      modal-class="modal right fade"
      dialog-class="modal-dialog modal-dialog-slideout modal-dialog-slideout-right modal-dialog-slideout-md"
      @hide="opportunity = null"
    >
      <OpportunityForm
        v-if="opportunity"
        :opportunity="opportunity"
        :customer="customer"
        :company="company"
        @saved="hideOpportunityModalAndReload()"
        @cancel="hideOpportunityModal($event)"
      />
    </b-modal>
  </div>
</template>

<script>
import { Container, Draggable } from 'vue-smooth-dnd';
import { deleteOpportunity, saveOpportunity, getOpportunities } from '@/api/opportunity';

export default {
  name: 'Opportunities',
  inject: ['$validator'],
  components: {
    OpportunityForm: () => import('./OpportunityForm.vue'),
    OpportunityItem: () => import('./OpportunityItem'),
    Container,
    Draggable
  },
  props: {
    opportunityState: {
      type: Object
    },
    customer: {
      type: Object
    },
    company: {
      type: Object
    },
    limit: {
      type: Number,
      default: 40
    },
    filter: {
      type: Object
    },
    sortParams: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      opportunity: null,
      canLoadMore: false,
      page: 1,
      annualizedValue: 0,
      listCount: 0,
      list: [],
      text: null,
      pending: false,
      loading: false,
      isNew: false
    };
  },
  computed: {
    opportunityStateLabel() {
      return this.opportunityState?.name;
    }
  },
  watch: {
    filter: {
      handler() {
        this.reloadOpportunities();
      },
      deep: true
    },
    sortParams: {
      handler() {
        this.reloadOpportunities();
      },
      deep: true
    }
  },
  async created() {
    this.loadOpportunities();
  },
  methods: {
    getChildPayload(index) {
      return {
        ...this.list[index]
      };
    },
    loadNextOpportunities() {
      this.page++;
      this.loadOpportunities();
    },
    async loadOpportunities() {
      try {
        this.loading = true;
        const params = {
          filter: this.filter,
          opportunityStateId: this.opportunityState?._id || null,
          customerId: this.customer?._id || null,
          companyId: this.company?._id || null,
          page: this.page,
          limit: this.limit,
          ...(this.sortParams || {})
        };
        // console.log('params: ', params);
        const { data, count, annualizedValue } = await getOpportunities(params);
        this.list = [...this.list, ...data];
        this.listCount = count;
        this.annualizedValue = annualizedValue;
        this.canLoadMore = this.list.length < count;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async onDrop(dropResult) {
      const { removedIndex, addedIndex, payload } = dropResult;
      if (Number.isInteger(removedIndex)) {
        const removed = this.list.splice(removedIndex, 1)[0];
        this.list = [...this.list];

        this.listCount--;
        this.annualizedValue = +(this.annualizedValue - this.calcOpportunityValue(removed)).toFixed(2);
      }
      if (Number.isInteger(addedIndex)) {
        let sortIndex;
        if (addedIndex === 0) {
          const first = this.list[0];
          sortIndex = first ? first.sortIndex + 100 : 0;
        } else if (addedIndex >= this.list.length) {
          const last = this.list[this.list.length - 1];
          sortIndex = last ? last.sortIndex - 100 : 0;
        } else {
          const prev = this.list[addedIndex - 1];
          const next = this.list[addedIndex];
          const prevSortIndex = prev ? prev.sortIndex : next ? next.sortIndex + 200 : 0;
          const nextSortIndex = next ? next.sortIndex : prev ? prev.sortIndex - 200 : 0;
          sortIndex = prevSortIndex - (prevSortIndex - nextSortIndex) / 2;
        }

        this.list.splice(addedIndex, 0, { ...payload, sortIndex });
        this.list = [...this.list];

        this.listCount++;
        this.annualizedValue = +(this.annualizedValue + this.calcOpportunityValue(payload)).toFixed(2);

        await saveOpportunity({
          _id: payload._id,
          sortIndex,
          opportunityStateId: this.opportunityState?._id || null
        });
      }
    },
    calcOpportunityValue(opportunity) {
      const value = opportunity?.value || 0;
      const multiply = opportunity?.recurrence === 'Monthly' ? 12 : 1;
      return multiply * value;
    },
    reloadOpportunities() {
      this.page = 1;
      this.list = [];
      this.listCount = 0;
      this.annualizedValue = 0;
      this.loadOpportunities();
    },
    showOpportunityModal() {
      this.$refs.modal.show();
    },
    hideOpportunityModal(event) {
      this.$refs.modal.hide();
    },
    hideOpportunityModalAndReload() {
      this.hideOpportunityModal();
      this.reloadOpportunities();
    },
    addNewOpportunity() {
      this.opportunity = {
        value: null,
        notes: null,
        sortIndex: (this.list[0]?.sortIndex || 0) + 100,
        confidence: 50,
        recurrence: 'One-time',
        customerId: null,
        opportunityStateId: this.opportunityState?._id || null
      };
      this.showOpportunityModal();
    },
    editOpportunity(opportunity) {
      this.opportunity = opportunity && JSON.parse(JSON.stringify(opportunity));
      this.showOpportunityModal();
    },
    async deleteOpportunity(opportunity) {
      await deleteOpportunity(opportunity);
      this.list = this.list.filter((e) => e._id !== opportunity._id);
      this.reloadOpportunities();
    }
  }
};
</script>
